import type { FilterPill } from '@/app/common/filters/surfaces/bar/pills/types.ts';
import type { BarLayout, BarPillContext } from '@/app/common/filters/surfaces/bar/resultsPills/barPills.ts';
import { assembleBarPills } from '@/app/common/filters/surfaces/bar/resultsPills/barPills.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { FilterOrder } from '@/app/common/filters/session/filterOrder.ts';
import { EMPTY_FILTER_ORDER } from '@/app/common/filters/session/filterOrder.ts';
import { getSearchPersona } from '@/app/common/filters/quickFilters/searchPersona.ts';
import { getPersonaQuickFilters } from '@/app/common/filters/quickFilters/personaQuickFilters.ts';
import { resolveQuickFilters } from '@/app/common/filters/quickFilters/resolveQuickFilters.ts';
import { createQuickFilterPill } from '@/app/common/filters/variants/evo/pills/QuickFilterPill.tsx';
import { orderEvoFilterBarPills } from '@/app/common/filters/variants/evo/pills/orderEvoFilterBarPills.ts';

type CreateEvoPillsProps = BarPillContext & {
  searchParams: SearchParams;
  filterOrder?: FilterOrder;
};

const createPills = (
  { searchParams, filterOrder = EMPTY_FILTER_ORDER, ...context }: CreateEvoPillsProps,
  layout: BarLayout
): FilterPill[] => {
  const groupPills = assembleBarPills(context, layout, { groupIcons: true }).map((pill) => {
    const selectionOrder = filterOrder.options[pill.id as string];
    return selectionOrder ? { ...pill, selectionOrder } : pill;
  });

  const groupPillIds = new Set(groupPills.map((pill) => pill.id as string));
  const persona = getSearchPersona({ searchParams, filtersGroups: context.filtersGroups });
  const quickFilterPills = resolveQuickFilters({
    filtersGroups: context.filtersGroups,
    t: context.t,
    quickFilters: getPersonaQuickFilters(persona),
  })
    .filter((resolved) => resolved.available)
    .map(createQuickFilterPill)
    .filter((pill) => groupPillIds.has(pill.id as string));

  return orderEvoFilterBarPills({ pills: groupPills, quickFilterPills, groupOrder: filterOrder.groups });
};

export const createEvoBarPills = (props: CreateEvoPillsProps): FilterPill[] => createPills(props, 'desktop');

export const createEvoMobileBarPills = (props: CreateEvoPillsProps): FilterPill[] => createPills(props, 'mobile');
